import React, { useState, useCallback, useEffect } from 'react';
import { 
  User, Link as LinkIcon, Plus, ChevronDown, ChevronRight, 
  Search, X, Check, MoreHorizontal 
} from 'react-feather';
import { API_URL, fetchWithAuth, getImageUrl } from '../../api';

// |||||||||||||||||||||||||||||||||||||||||||||||||||||||||||
// HELPER: CONVERTIR ARRAY PLANO A ÁRBOL (NESTED)
// |||||||||||||||||||||||||||||||||||||||||||||||||||||||||||
const buildHierarchy = (items, parentId = null) => {
    return items
        .filter(item => item.parent_id === parentId)
        .map(item => ({
            ...item,
            children: buildHierarchy(items, item.id)
        }));
};

// |||||||||||||||||||||||||||||||||||||||||||||||||||||||||||
// COMPONENTE DE FILA (RECURSIVO)
// |||||||||||||||||||||||||||||||||||||||||||||||||||||||||||
const TeamMemberRow = ({ member, currentUserId, isCreator, onAddClick, depth = 0 }) => {
    const [isExpanded, setIsExpanded] = useState(true); // Por defecto abierto
    const [copied, setCopied] = useState(false);
    
    const isMe = String(member.user_id) === String(currentUserId);
    const hasChildren = member.children && member.children.length > 0;

    // Lógica de permisos (Misma que tenías antes)
    let canAdd = false;
    if (isCreator) {
        canAdd = member.depth < 2; 
    } else {
        // Regla: puedo agregar si es mi nodo o uno inferior a mí
        // Para simplificar en la vista de lista: Si soy yo O soy el creador, y no es el último nivel.
        // Aquí asumimos que si renderizamos el botón, el backend validará.
        // Ajustamos visualmente:
        canAdd = (isMe || isCreator) && member.depth < 2;
    }
    
    // Si NO soy el creador y NO soy yo, oculto el botón de agregar para no confundir,
    // a menos que sea un sub-nodo mío (esto requiere saber mi depth, lo simplificamos a isMe para esta vista simple)
    // Para una lógica perfecta, deberías pasar "myDepth" hacia abajo.
    
    const copyLink = (e) => {
        e.stopPropagation();
        const link = `${window.location.origin}/events/${member.event_id}/request-qr?ref=${member.user_id}`; 
        navigator.clipboard.writeText(link);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="w-full select-none animate-in fade-in duration-300">
            {/* --- LA FILA DEL USUARIO --- */}
            <div 
                className={`
                    relative flex items-center justify-between p-3 my-1 rounded-xl border transition-all
                    ${isMe 
                        ? 'bg-indigo-900/20 border-indigo-500/30' 
                        : 'bg-white/5 border-transparent hover:bg-white/10 hover:border-white/10'}
                `}
                style={{ marginLeft: `${depth * 24}px` }} // Indentación visual
            >
                {/* LÍNEAS GUÍA PARA NIVELES PROFUNDOS */}
                {depth > 0 && (
                    <div className="absolute -left-[12px] top-1/2 -translate-y-1/2 w-3 h-[1px] bg-white/10"></div>
                )}
                {depth > 0 && (
                    <div className="absolute -left-[12px] -top-5 h-[calc(50%+20px)] w-[1px] bg-white/10"></div>
                )}

                <div className="flex items-center gap-3 flex-1 overflow-hidden" onClick={() => setIsExpanded(!isExpanded)}>
                    {/* Botón Colapsar (Solo si tiene hijos) */}
                    <button 
                        className={`text-gray-500 hover:text-white transition-colors ${!hasChildren && 'invisible'}`}
                        onClick={(e) => { e.stopPropagation(); setIsExpanded(!isExpanded); }}
                    >
                        {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </button>

                    {/* Avatar */}
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center overflow-hidden border ${isMe ? 'border-indigo-400' : 'border-white/10 bg-gray-800'}`}>
                        {member.profile_image ? (
                            <img src={getImageUrl(member.profile_image)} alt="" className="w-full h-full object-cover"/>
                        ) : (
                            <User size={14} className="text-gray-400"/>
                        )}
                    </div>

                    {/* Info */}
                    <div className="flex flex-col min-w-0">
                        <div className="flex items-center gap-2">
                            <span className={`text-sm font-medium truncate ${isMe ? 'text-white' : 'text-gray-300'}`}>
                                {member.name || member.username}
                            </span>
                            {isMe && <span className="text-[9px] bg-indigo-500 text-white px-1 rounded-sm">TÚ</span>}
                        </div>
                        <span className="text-[10px] text-gray-500 uppercase tracking-wider font-medium">
                            {member.role_label} • Nivel {member.depth + 1}
                        </span>
                    </div>
                </div>

                {/* Acciones */}
                <div className="flex items-center gap-2">

                    {/* Agregar Miembro */}
                    {canAdd && (
                        <button 
                            onClick={(e) => { e.stopPropagation(); onAddClick(member.id); }}
                            className="flex items-center gap-1 pl-2 pr-3 py-1.5 rounded-lg bg-white/5 hover:bg-white text-gray-300 hover:text-black text-xs font-bold transition-all border border-white/5 hover:border-white"
                        >
                            <Plus size={14} /> <span className="hidden sm:inline">Equipo</span>
                        </button>
                    )}
                </div>
            </div>

            {/* --- RECURSIVIDAD (HIJOS) --- */}
            {isExpanded && hasChildren && (
                <div className="relative">
                    {/* Línea vertical conectora para los hijos */}
                    <div 
                        className="absolute w-[1px] bg-white/10"
                        style={{ left: `${(depth * 24) + 12}px`, top: '0', bottom: '15px' }}
                    ></div>
                    
                    {member.children.map(child => (
                        <TeamMemberRow 
                            key={child.id} 
                            member={child} 
                            currentUserId={currentUserId}
                            isCreator={isCreator}
                            onAddClick={onAddClick}
                            depth={depth + 1}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

// |||||||||||||||||||||||||||||||||||||||||||||||||||||||||||
// COMPONENTE PRINCIPAL
// |||||||||||||||||||||||||||||||||||||||||||||||||||||||||||
const EventTeamListView = ({ eventId, currentUserId, isCreator }) => {
    const [treeData, setTreeData] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Estados Modal (Igual que antes)
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [targetParentId, setTargetParentId] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isAdding, setIsAdding] = useState(false);

    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            const response = await fetchWithAuth(`${API_URL}/events/${eventId}/team-hierarchy`);
            const rawNodes = response || [];
            
            // Si soy creador, veo todo desde la raíz (padres null).
            // Si soy promotor, el backend probablemente devuelve mi nodo y mis hijos.
            // Para armar el árbol correctamente, necesitamos encontrar las "raíces" de lo que devolvió el backend.
            
            // Estrategia: Buscar los nodos cuyo parent_id NO existe en el array devuelto. Esas son las raíces visuales.
            const allIds = new Set(rawNodes.map(n => n.id));
            const rootNodes = rawNodes.filter(n => !n.parent_id || !allIds.has(n.parent_id));

            // Construimos el árbol partiendo de esas raíces
            const tree = rootNodes.map(root => ({
                ...root,
                children: buildHierarchy(rawNodes, root.id)
            }));
            
            setTreeData(tree);

        } catch (error) {
            console.error("Error loading team:", error);
        } finally {
            setLoading(false);
        }
    }, [eventId]);

    useEffect(() => { loadData(); }, [loadData]);

    // --- MODAL HANDLERS ---
    const openAddModal = (parentId) => {
        setTargetParentId(parentId);
        setSearchQuery('');
        setSearchResults([]);
        setIsModalOpen(true);
    };

    const handleSearch = async (query) => {
        setSearchQuery(query);
        if (query.length < 2) return setSearchResults([]);
        try {
            const res = await fetchWithAuth(`${API_URL}/users/search?query=${query}`);
            setSearchResults(Array.isArray(res) ? res : (res.data || []));
        } catch (e) { console.error(e); }
    };

    const handleAddUser = async (username) => {
        try {
            setIsAdding(true);
            await fetchWithAuth(`${API_URL}/events/${eventId}/collaborators`, {
                method: 'POST',
                body: JSON.stringify({ username, parent_collab_id: targetParentId })
            });
            setIsModalOpen(false);
            setTimeout(() => loadData(), 200);
        } catch (error) {
            alert(error.message || "Error");
        } finally {
            setIsAdding(false);
        }
    };

    return (
        <div className="w-full min-h-[60vh] mt-6">
            
            {/* Cabecera */}
            <div className="flex justify-between items-end mb-6 border-b border-white/10 pb-4">
                <div>
                    <h2 className="text-xl font-bold text-white">Mi Equipo</h2>
                    <p className="text-sm text-gray-400 mt-1">
                        {isCreator ? "Gestiona toda la estructura de ventas." : "Gestiona tus promotores asignados."}
                    </p>
                </div>
                {isCreator && (
                    <button 
                        onClick={() => openAddModal(null)}
                        className="flex items-center gap-2 px-4 py-2 bg-white text-black text-sm font-bold rounded-full hover:scale-105 transition-transform"
                    >
                        <Plus size={16} /> Nueva Cabeza
                    </button>
                )}
            </div>

            {/* Lista Principal */}
            <div className="bg-[#121212] border border-white/5 rounded-2xl p-4 sm:p-6 shadow-xl min-h-[300px]">
                {loading ? (
                    <div className="flex flex-col items-center justify-center py-20 text-gray-500 animate-pulse">
                        <User size={32} className="mb-2 opacity-50"/>
                        <span>Cargando equipo...</span>
                    </div>
                ) : treeData.length === 0 ? (
                    <div className="text-center py-20 bg-white/5 rounded-xl border border-dashed border-white/10">
                        <p className="text-gray-300 font-medium">Aún no hay equipo formado.</p>
                        {isCreator && <p className="text-sm text-gray-500 mt-1">Comienza agregando una Cabeza de Equipo.</p>}
                    </div>
                ) : (
                    <div className="flex flex-col gap-1">
                        {treeData.map(node => (
                            <TeamMemberRow 
                                key={node.id}
                                member={node}
                                currentUserId={currentUserId}
                                isCreator={isCreator}
                                onAddClick={openAddModal}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* --- MODAL (REUTILIZADO) --- */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in fade-in duration-200">
                    <div className="bg-[#111] border border-white/10 rounded-2xl w-full max-w-md shadow-2xl flex flex-col max-h-[80vh]">
                        <div className="p-5 border-b border-white/10 flex justify-between items-center bg-white/5">
                            <h3 className="text-white font-medium">
                                {targetParentId ? "Agregar Promotor" : "Nueva Cabeza"}
                            </h3>
                            <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-white">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="p-5 overflow-hidden flex flex-col flex-1">
                            <div className="relative mb-4">
                                <Search className="absolute left-3 top-3.5 text-gray-500" size={18} />
                                <input 
                                    type="text"
                                    autoFocus
                                    value={searchQuery}
                                    onChange={(e) => handleSearch(e.target.value)}
                                    placeholder="Buscar usuario..."
                                    className="w-full bg-black border border-white/20 rounded-xl py-3 pl-10 pr-4 text-white focus:outline-none focus:border-white/50"
                                />
                            </div>
                            <div className="flex-1 overflow-y-auto space-y-2">
                                {searchResults.map((user) => (
                                    <button
                                        key={user.username}
                                        onClick={() => handleAddUser(user.username)}
                                        disabled={isAdding}
                                        className="w-full flex items-center justify-between p-3 rounded-xl hover:bg-white/5 group border border-transparent hover:border-white/10 text-left"
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center overflow-hidden">
                                                {user.profile_image_url ? (
                                                    <img src={getImageUrl(user.profile_image_url)} className="w-full h-full object-cover"/>
                                                ) : <User size={14} className="text-gray-400"/>}
                                            </div>
                                            <div>
                                                <p className="text-white text-sm">@{user.username}</p>
                                                <p className="text-xs text-gray-500">{user.name}</p>
                                            </div>
                                        </div>
                                        <div className="opacity-0 group-hover:opacity-100 text-white"><Plus size={18}/></div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EventTeamListView;